import { useNavigate } from "react-router-dom";
import { Button, Popconfirm, Tooltip, Descriptions } from 'antd';
import { EyeFilled, EditFilled, DeleteFilled, MergeFilled } from '@ant-design/icons';

const Columns = (hasPermission, handleOpenModal, handleDelete, dependenciesTableFilter, isSuperuser, hasAnyRole) => {
  const navigate = useNavigate();
  const { 
    filterTypes, 
    filterAssets, 
    filterAreas, 
    filterNames,
    redirectAssetsToDependencies, 
    dashboardAssetsFilter, 
    dashboardAreasFilter,
    dashboardDependenciesFilter
  } = dependenciesTableFilter();

  return [
    { 
      title: 'Activos', 
      dataIndex: ['asset', 'name'], 
      key: 'asset',
      sorter: (a, b) => (a.asset?.name || '').localeCompare(b.asset?.name || ''),
      filters: filterAssets?.map(asset => ({ text: asset?.name || 'N/A', value: asset?.name || 'N/A' })) || [],
      defaultFilteredValue: (
        dashboardAssetsFilter?.length
          ? dashboardAssetsFilter
          : []
      ).concat(
        redirectAssetsToDependencies
          ? [redirectAssetsToDependencies]
          : []
      ) || undefined,
      onFilter: (value, record) => (record.asset?.name || '').includes(value),
      render: (text) => text || 'N/A',
      onHeaderCell: () => ({
        style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' },
        className: 'custom-filter-icon'
      }),
      showSorterTooltip: false,
      width: '15%',
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      defaultFilteredValue: dashboardDependenciesFilter,
      filters: filterNames?.map(name => ({
        text: name,
        value: name
      })) || [],
      onFilter: (value, record) => record.name === value,
      onHeaderCell: () => ({ style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' } }),
      showSorterTooltip: false,
      width: '20%',
    },
    { 
      title: 'Descripción', 
      dataIndex: 'description', 
      key: 'description',
      sorter: (a, b) => a.description.localeCompare(b.description),
      onHeaderCell: () => ({ style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' } }),
      ellipsis: true,
      render: (text) => {
        if (!text) return <span style={{ color: '#999' }}>Sin descripción</span>;
    
        const truncatedText = text.length > 50 ? `${text.substring(0, 50)}...` : text;
    
        return text.length > 40 ? (
          <Tooltip title={text}>
            <span>{truncatedText}</span>
          </Tooltip>
        ) : (
          <span>{truncatedText}</span>
        );
      },
      showSorterTooltip: false,
      width: '25%',
    },
    {
      title: 'Atributos',
      dataIndex: 'atributes',
      key: 'atributes',
      render: (atributes) => {
        const validAttributes = atributes?.filter(attr => attr?.atribute?.key) || [];
      
        if (!validAttributes.length) return <span style={{ color: '#999' }}>N/A</span>;
      
        return (
          <Tooltip
            title={
              <div style={{ maxWidth: 300 }}>
                <ul style={{ paddingLeft: 16, margin: 0 }}>
                  {validAttributes.map((attr, index) => (
                    <li key={index} style={{ marginBottom: 4 }}>
                      <strong>{attr.atribute.key}:</strong>{' '}
                      <span style={{ marginLeft: 5 }}>{attr.value || 'N/A'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            }
          >
            <span style={{ color: '#006939',  }}>
              Ver Atributos ({validAttributes.length})
            </span>
          </Tooltip>
        );
      },           
      onHeaderCell: () => ({ style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' } }),
      showSorterTooltip: false,
      width: '15%',
    },
    { 
      title: 'Tipo', 
      dataIndex: ['type', 'name'], 
      key: 'type',
      sorter: (a, b) => (a.type?.name || '').localeCompare(b.type?.name || ''),
      filters: filterTypes?.map(type => ({ text: type?.name || 'N/A', value: type?.name || 'N/A' })) || [],
      onFilter: (value, record) => (record.type?.name || '').includes(value),
      render: (text) => text || 'N/A',
      onHeaderCell: () => ({ style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' }, className: 'custom-filter-icon' }),
      showSorterTooltip: false,
      width: '15%',
    },
    {
      title: "Responsable",
      dataIndex: ["asset", "area"],
      key: "asset_area",
      sorter: (a, b) => (a.asset?.area?.name || '').localeCompare(b.asset?.area?.name || ''),
      defaultFilteredValue: dashboardAreasFilter,
      filters: filterAreas?.map(area => ({
        text: area.name,
        value: area.name
      })),
      onFilter: (value, record) => (record.asset?.area?.name || '') === value,
      render: (text, record) => record.asset?.area?.name || 'N/A',
      onHeaderCell: () => ({
        style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold' },
        className: 'custom-filter-icon'
      }),
      showSorterTooltip: false,
      width: '12%',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => {
        const canUpdate = isSuperuser || hasPermission("dependencies", "update", record.asset?.area?.id);
        const canDelete = isSuperuser || hasPermission("dependencies", "delete", record.asset?.area?.id);
        const canAssignment = isSuperuser || hasPermission("assignment", "create", record.asset?.area?.id);

        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <Button 
              type="link" 
              style={{ color: '#006939', fontSize: 18,  padding: '0px 4px' }} 
              onClick={() => navigate(`/dependencies/${record.code}`)}
            >
              <Tooltip title="Ver Dependencia"><EyeFilled /></Tooltip>
            </Button>
            <Button
              type="link" 
              disabled={!canUpdate}
              style={{ color: canUpdate ? '#006939' : '#999999', fontSize: 18, padding: '0px 4px' }}
              onClick={() => handleOpenModal(record)}>
              <Tooltip title="Editar Dependencia"><EditFilled /></Tooltip>
            </Button>
            <Button 
              type="link"
              disabled={!canAssignment}
              style={{ color: canAssignment ? '#F5A623' : '#999999', fontSize: 18, padding: '0px 2px' }}
              onClick={() => navigate(`/assignment?tab=1&asset=${encodeURIComponent(record.asset.name)}&dependency=${encodeURIComponent(record.name)}`)}
            >
              <Tooltip title="Asignar Controles">
                <MergeFilled />
              </Tooltip>
            </Button>
            <Popconfirm
              disabled={!canDelete}
              overlayClassName="custom-popconfirm"
              title="¿Seguro que deseas eliminar esta Dependencia?"
              onConfirm={() => handleDelete(record.code)}
              okText="Sí"
              cancelText="No"
            >
              <Button
                type="link"
                disabled={!canDelete}
                style={{ color: canDelete ? '#006939' : '#999999', fontSize: 18, padding: '0px 4px' }}
              >
                <Tooltip title="Eliminar Dependencia" placement='left'><DeleteFilled /></Tooltip>
              </Button>
            </Popconfirm>
          </div>
        )
      },
      onHeaderCell: () => ({
        style: { backgroundColor: '#E0F8AD', color: '#006939', fontWeight: 'bold', textAlign: 'center' }
      }),
      align: 'center',
      width: '10%',
    }
  ];
};

export default Columns;